<template> 

    <!-- Map -->
    <div class="map">
        <div class="map__leaflet" ref="map"></div>
    </div>
</template>
<script>

    // Dependancies
    import Vue from 'vue'
    import Leaflet from 'leaflet'
    import { isEqual } from 'lodash'
    import { mapActions, mapGetters } from 'vuex'

    // Sub Dependancies
    import 'leaflet/dist/leaflet.css'
    import '../vendor/PitouGames/Leaflet.Polyline.SnakeAnim'
    import 'leaflet.smooth_marker_bouncing'
    import 'leaflet.snogylop'

    export default {
        data() {
            return {

                // Map
                leafletMap: null,

                // Icons
                icons: [],

                // Layers
                animatedLineLayers: [],
                boundaryLayerGroup: Leaflet.layerGroup(),
                markersLayerGroup: Leaflet.layerGroup(),
            }
        },

        computed: {
            ...mapGetters({
                configuration: 'configuration',
                incrementDecrementButton: 'incrementDecrementButton',
                previousTourStep: 'previousTourStep',
                touring: 'touring',
                tourStep: 'tourStep'
            }),
            
            /*
             * Returns all steps
             *
             * @returns {array}
             */
            steps() {
                return this.configuration.steps
            },

            /*
             * Returns the current step
             *
             * @returns {object}
             */
            step() {
                return this.steps[this.tourStep]
            },

            /*
             * Returns the previous step
             *
             * @returns {object}
             */
            previousStep() {
                return this.steps[this.previousTourStep]
            },

            /*
             * Returns all of the previous steps
             *
             * @returns {array}
             */
            previousSteps() {
                return this.steps.filter((step, index) => index < this.tourStep) 
            },

            /*
             * Returns animated lines for this particular step
             *
             * @returns {array}
             */
            stepAnimatedLines() {
                return this.animatedLineLayers.filter(layer => layer.options.stepId == this.tourStep)
            },
        },

        methods: {
            ...mapActions({
                setIncrementDecrementButton: 'setIncrementDecrementButton',
                setTouring: 'setTouring',
                setTourStep: 'setTourStep',
                setTravelling: 'setTravelling'
            }),

            /*
             * Instantiate the Leaflet Map
             *
             * @returns {void}
             */
            instantiateMap() {

                // Instantiate Leaflet Map
                this.leafletMap = Leaflet.map(this.$refs.map, {
                    minZoom: this.configuration.settings.minZoom,
                    zoomSnap: this.configuration.settings.zoomSnap,
                    zoomControl: false
                }).setView(
                    [
                        this.configuration.settings.position.latitude, 
                        this.configuration.settings.position.longitude
                    ], 
                    this.configuration.settings.position.zoom
                )
            },

            /*
             * Adds the Tile Layer to the Leaflet Map
             *
             * @returns {void}
             */
            addTileLayer() {

                // Add Mapbox Tile Layer with attribution
                Leaflet.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
                    attribution: 'Map data &copy; <a target="_blank" rel="noopener nofollow" href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a target="_blank" rel="noopener nofollow" href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>, Imagery © <a target="_blank" rel="noopener nofollow" href="https://www.mapbox.com/">Mapbox</a>',
                    id: this.configuration.settings.mapbox.studioId,
                    tileSize: 512,
                    zoomOffset: -1,
                    accessToken: this.configuration.settings.mapbox.accessToken
                }).addTo(this.leafletMap)
            },

            /*
             * Adds the element layers to the Leaflet Map
             *
             * @returns {void}
             */
            addElementLayers() {

                // Add the marker layer to the leaflet map
                this.markersLayerGroup.addTo(this.leafletMap)

                // Add the Boundary Layer Group to Map
                this.boundaryLayerGroup.addTo(this.leafletMap)
            },

            /*
             * Adds dynamic icons so they can be accessed by the Leaflet Map
             *
             * @returns {void}
             */
            addIcons() {

                // Verify that the configuration includes icons
                if (this.configuration.icons) {

                    // Foreach configuration icon
                    this.configuration.icons.forEach((icon) => {

                        // Register the icon
                        this.icons[icon.id] = Leaflet.icon({
                            iconUrl: icon.source,
                            iconSize: [
                                icon.size.width,
                                icon.size.height
                            ],
                            iconAnchor: [
                                icon.anchor.x,
                                icon.anchor.y
                            ]
                        })
                    })
                }
            },
            
            /*
             * Sets the initial map position
             *
             * @returns {void}
             */
            setInitialMapPosition() {

                // Set map view
                this.leafletMap.setView(
                    [
                        this.configuration.settings.position.latitude, 
                        this.configuration.settings.position.longitude
                    ],
                    this.configuration.settings.position.zoom
                )
            },

            /*
             * Sets the map position based on the current step
             *
             * @returns {void}
             */
            setMapPosition() {
                
                // Fly to the step position
                this.leafletMap.flyTo(
                    [
                        this.step.position.latitude, 
                        this.step.position.longitude
                    ],
                    this.step.position.zoom
                )
            },
            
            /*
             * When the tour step value is changed, update the map with the step data
             *
             * @returns {void}
             */
            onTourStepChange() {
                
                // Stops any ongoing map events
                this.stopMapEvents()

                // Verify that there is a previous step
                if (this.previousStep) {

                    // Determine if the previous step boundary is not the same as the current step boundary
                    if (!isEqual(this.previousStep.boundary, this.step.boundary)) {

                        // The previous step boundary is not the same as the current boundary, clear the boundary
                        this.clearBoundary()
                    }
                }

                // Clear any temporary or future step animated lines
                this.clearAnimatedLines()

                // Clear any temporary or future step markers
                this.clearMarkers()
                
                // Determine if the map position requires setting
                if (this.shouldSetMapPosition()) {       
                
                    // The map position is currently not visible, set the map position
                    this.setMapPosition()

                    // Register an event, on leaflet map zoomend, set the map elements
                    this.leafletMap.on('zoomend', this.setMapElements)
                } else {

                    // The map position is visibible, set the map elements
                    this.setMapElements()
                }
            },

            /*
             * Stops any ongoing map events, such as line animations and bouncing markers
             *
             * @returns {void}
             */
            stopMapEvents() {

                // Remove the on leaflet zoomend event (otherwise it will trigger everytime the user zooms)
                this.leafletMap.off('zoomend', this.setMapElements)
                
                // Stop all markers from bouncing
                Leaflet.Marker.stopAllBouncingMarkers()

                // Foreach animated line, reset the polyline
                this.animatedLineLayers.forEach((animatedLineLayer) => {

                    // Reset the animated line
                    animatedLineLayer.snakeReset()
                })
            },

            /*
             * Determines whether the step position is visible on the map and whether we need to the set the map position
             *
             * @returns {boolean}
             */
            shouldSetMapPosition() {

                // Return whether the leaflet map contains the new step location and whether the zoom matches
                return this.step.position && (
                    !this.leafletMap.getBounds().contains([this.step.position.latitude, this.step.position.longitude])
                    || this.leafletMap.getZoom() !== this.step.position.zoom
                )
            },

            /*
             * Sets the map elements for the current step
             *
             * @returns {void}
             */
            setMapElements() {

                // Set the map animated lines
                this.setAnimatedLines()

                // Set the map boundary
                this.setBoundary()

                // Set the map markers
                this.setMarkers()

                // Wait for UI to update
                Vue.nextTick(() => {
                    
                    // Set the increment/decrement button to false
                    this.setIncrementDecrementButton(false)

                    // Remove the on leaflet zoomend event (otherwise it will trigger everytime the user zooms)
                    this.leafletMap.off('zoomend', this.setMapElements)
                })        
            },

            /*
             * Sets the boundary element for the current step
             *
             * @returns {void}
             */
            setBoundary() {
                
                // Verify that the step has a boundary and that the map does not have a boundary
                if (this.step.boundary && !this.boundaryLayerGroup.getLayers().length) {
                
                    // Create the GEO-Json boundary
                    Leaflet.geoJson(this.step.boundary.geoJson, {
                        coordsToLatLng: (coords) => {
                            return new Leaflet.LatLng(coords[0], coords[1], coords[2])
                        },
                        invert: this.step.boundary.settings.invert,
                        finalOpacity: this.step.boundary.style.opacity,
                        style: {
                            color: this.step.boundary.style.color,
                            weight: this.step.boundary.style.weight,
                            fillOpacity: 0,
                            opacity: 0,
                        }
                    }).addTo(this.boundaryLayerGroup)

                    // Fade in the Layer Group
                    this.fadeInLayerGroup(this.boundaryLayerGroup)
                }
            },

            /*
             * Sets the animated line elements for the current step
             *
             * @returns {void}
             */
            setAnimatedLines() {

                // Ensure that the animated lines for the previous steps exist on the map, foreach previous step
                this.previousSteps.forEach((step, index) => {

                    // Fetch instantiated animated lines for this particular loop instance
                    const stepAnimatedLines = this.animatedLineLayers.filter(animatedLineLayer => animatedLineLayer.options.stepId == index)
                    
                    // Verify that no animated lines have been instantiated on the map for this step
                    if (!stepAnimatedLines.length) {
                        
                        // Verify that the loop instance step has animated lines
                        if (step.animatedLines) {
                            
                            // Foreach loop instance step animated line
                            step.animatedLines.forEach((animatedLine) => {

                                // Verify that the animate line is not temporary
                                if (!animatedLine.settings.temporary) {

                                    // Add the animated line to the map
                                    this.addAnimatedLine(animatedLine, index)
                                }
                            })
                        }
                    }
                })
                
                // Verify that the step has animated lines
                if (this.step.animatedLines.length) {

                    // Foreach step animated line
                    this.step.animatedLines.forEach((animatedLine) => {

                        // Add the animated line to the map
                        this.addAnimatedLine(animatedLine, this.tourStep)

                        // Determine if the animated line should be followed
                        if (animatedLine.settings.follow) {

                            // The animated line should be followed, register an event to update the map position while the line is animated
                            this.animatedLineLayers[(this.animatedLineLayers.length - 1)].on('snakeIn', this.onSnakeIn)

                            // Register an event to set travelling to false once the line has finished animating
                            this.animatedLineLayers[(this.animatedLineLayers.length - 1)].on('snakeInEnd', this.onSnakeInEnd)
                        } else {
                        
                            // The animated line should not be followed, set travelling to false
                            this.setTravelling(false)
                        }
                    })

                    // Foreach current step animated line
                    this.stepAnimatedLines.forEach((stepAnimatedLine) => {

                        // Determine if the current tour step is greater than the previous tour step (travelling forward) and that the tour step change was a result of the increment/decrement button
                        if (this.tourStep > this.previousTourStep && this.incrementDecrementButton) {

                            // Animated the line
                            stepAnimatedLine.snakeIn()
                        } else {

                            // The user is travelling in reverse, simply show the line, set travelling to false
                            this.setTravelling(false)
                        }
                    })
                } else {

                    // The step has no animated lines, set travelling to false
                    this.setTravelling(false)
                }
            },

            /*
             * Adds an animated line to the map
             *
             * @param {object} data
             * @param {integer} tourStep
             * @returns {void}
             */
            addAnimatedLine(data, tourStep) {

                // Map the animated line coordinates into a Leaflet LatLng elements array
                for (var latlngIndex = 0, latlngs = [], len = data.coordinates.length; latlngIndex < len; latlngIndex++) {
                    latlngs.push(new Leaflet.LatLng(data.coordinates[latlngIndex][0], data.coordinates[latlngIndex][1]))
                }

                // Create the animated line
                const animatedLine = Leaflet.polyline(latlngs, { 
                    stepId: tourStep,
                    color: data.style.color, 
                    weight: data.style.weight, 
                    snakingSpeed: data.settings.snakingSpeed,
                    opacity: data.style.opacity,
                    temporary: data.settings.temporary
                })
                
                // Add the animated line to the leaflet map
                animatedLine.addTo(this.leafletMap)

                // Push the animated line into the animated lines array
                this.animatedLineLayers.push(animatedLine)

                // Return the animated line
                return animatedLine
            },

            /*
             * While the animated line is animating, update the map to follow the line
             *
             * @param {object} event
             * @returns {void}
             */
            onSnakeIn(event) {
                this.leafletMap.setView(event)
            },

            /*
             * Once the animated line has finished animating, set travelling to false
             * @returns {void}
             */
            onSnakeInEnd() {
                this.setTravelling(false)
            },

            /*
             * Sets the marker elements for the current step
             *
             * @returns {void}
             */
            setMarkers() {

                // Ensure that the markers for the previous steps exist on the map, foreach previous step
                this.previousSteps.forEach((step, index) => {

                    // Fetch instantiated marker for this particular loop instance
                    const marker = this.markersLayerGroup.getLayers().find(marker => marker.options.stepId == index)

                    // Verify that no marker has been instantiated on the map for this step
                    if (!marker) {

                        // Verify that the loop instance step has a marker
                        if (step.modal.marker) {

                            // Add the marker to the map (setting as modal marker and surpressing bounce)
                            this.addMarker(step.modal.marker, index, true, true)
                        }
                    }
                })

                // Verify that the step has a modal marker
                if (this.step.modal.marker) {
                
                    // Add the marker to the map (setting as modal marker and surpressing bounce only if travelling in reverse)
                    this.addMarker(this.step.modal.marker, this.tourStep, true, (this.tourStep < this.previousTourStep || !this.incrementDecrementButton))
                }
                
                // Verify that the step has markers
                if (this.step.markers.length) {

                    // Add any temporary markers for this step
                    this.step.markers.forEach((marker) => {
                        
                        // Add the marker to the map
                        this.addMarker(marker, this.tourStep)
                    })
                }
            },

            /*
             * Sets all modal markers for all steps
             *
             * @returns {void}
             */
            setAllMarkers() {

                // Verify that there are steps
                if (this.steps.length) {

                    // Foreach step
                    this.steps.forEach((step, index) => {
                        
                        // Verify that the step has a modal marker
                        if (step.modal.marker) {

                            // Add the marker to the map (setting as modal marker and surpressing bounce)
                            this.addMarker(step.modal.marker, index, true, true)
                        }
                    })
                }
            },

            /*
             * Adds a marker to the map
             *
             * @param {object} data
             * @param {integer} tourStep
             * @param {boolean} isModal
             * @param {boolean} disableBounce
             * @returns {void}
             */
            addMarker(data, tourStep, isModal = false, disableBounce = false) {

                const modalMarker = Leaflet.marker([
                    data.position.latitude,
                    data.position.longitude
                ], {
                    stepId: tourStep,
                    icon: this.icons[data.largeIconId],
                    bounce: data.bounce,
                    modal: isModal,
                    opacity: 0
                })
                .bindTooltip(data.name)
                .addTo(this.markersLayerGroup)
                .on('click', this.onMarkerClick)

                this.fadeInMarker(modalMarker, disableBounce)
            },

            /*
             * On map marker click, update the tour step
             *
             * @param {object} event
             * @returns {void}
             */
            onMarkerClick(event) {

                // Verify that the marker is a modal marker
                if (event.target.options.modal) {

                    // Set touring to true
                    this.setTouring(true)

                    // Set the tour step to the marker clicked
                    this.setTourStep(event.target.options.stepId)
                }
            },

            /*
             * Clears the map of all elements
             *
             * @returns {void}
             */
            clearMap() {

                // Stops any ongoing map events
                this.stopMapEvents()

                // Clear the boundary
                this.clearBoundary()

                // Clear the animated lines
                this.clearAnimatedLines()

                // Clear the map markers
                this.clearMarkers()
            },

            /*
             * Clear all layers from the boundary group
             */
            clearBoundary() {

                // Clear existing layers
                this.boundaryLayerGroup.clearLayers()
            },

            /*
             * Clear animated lines from the animated lines array
             */
            clearAnimatedLines() {

                // Instantiate temporary animated line layer array
                const temporaryAnimatedLineLayers = []

                // Foreach animated line
                this.animatedLineLayers.forEach((animatedLineLayer) => {

                    // If the animated line step is greater than the current step, or if the line is temporary
                    if (animatedLineLayer.options.stepId > this.tourStep || animatedLineLayer.options.temporary) {
                        
                        // Remove the animated line from the map
                        this.leafletMap.removeLayer(animatedLineLayer)

                        // Reset the animated line
                        animatedLineLayer.snakeReset()
                    } else {

                        // The line is not temporary and past, push it to the temporary array
                        temporaryAnimatedLineLayers.push(animatedLineLayer)
                    }
                })

                // Set the animated line layers array to the temporary array
                this.animatedLineLayers = temporaryAnimatedLineLayers
            },
            
            /*
             * Clear layers from the markers group
             *
             * @returns {void}
             */
            clearMarkers() {

                // Foreach markers layer
                this.markersLayerGroup.getLayers().forEach((marker) => {

                    // If the marker step is greater than the current step, or if the marker not a modal marker
                    if (marker.options.stepId > this.tourStep || !marker.options.modal) {

                        // Remove the marker from the markers layer group
                        marker.removeFrom(this.markersLayerGroup)
                    }
                })
            },

            /*
             * Fades in a layer group using intervals
             *
             * @param {object} layerGroup
             * @returns {void}
             */
            fadeInLayerGroup(layerGroup) {
                
                // Instantiate array variable for storing layer intervals
                const intervals = []

                // Foreach layer group layer
                layerGroup.getLayers().forEach((layer, index) => {
                
                    // Instantiate current opacity variable
                    let currentOpacity = 0

                    // Set interval for this specific layer
                    intervals[index] = setInterval(() => {
                        
                        // Update the layer style with the current opacity
                        layer.setStyle({
                            fillOpacity: currentOpacity,
                            opacity: currentOpacity
                        })
                        
                        // Determine if the current opacity is greater than or equal to the final opacity
                        if (currentOpacity >= layer.options.finalOpacity) {

                            // The current opacity has reached the final opacity, clear the interval (stop processing)
                            clearInterval(intervals[index])
                        }

                        // Update the current opacity by 2%
                        currentOpacity += (layer.options.finalOpacity / 50)
                    }, 1) // Execute every millisecond
                })
            },

            /*
             * Fades in a marker using intervals
             *
             * @param {object} marker
             * @param {boolean} disableBounce
             * @returns {void}
             */
            fadeInMarker(marker, disableBounce = false) {

                // Instantiate variable for storing interval
                let interval = null

                // Determine if the marker has been set to bounce and ensure that the bounce effect has not been disabled
                if (marker.options.bounce && !disableBounce) {

                    // Bounce the marker
                    marker.bounce(1)
                }

                // Instantiate current opacity variable
                let currentOpacity = 0

                // Update interval for this marker
                interval = setInterval(() => {
                
                    // Update the marker style with the current opacity
                    marker.setOpacity(currentOpacity)
                    
                    // Determine if the current opacity is greater than or equal to 1
                    if (currentOpacity >= 1) {

                        // The current opacity is opaque, clear the interval (stop processing)
                        clearInterval(interval)
                    }

                    // Update the current opacity by 2%
                    currentOpacity += 0.02
                }, 1) // Execute every millisecond
            }
        },

        mounted() {

            // Instantiate the Leaflet Map
            this.instantiateMap()

            // Add the Mapbox Tile Layer
            this.addTileLayer()

            // Add the Marker Layer
            this.addElementLayers()

            // Add icons to the map
            this.addIcons()
        },

        watch: {

            /*
             * Upon tourStep value change
             *
             * @param {integer} newStep
             * @param {integer} oldStep
             * @returns {void}
             */
            tourStep(newStep, oldStep) {

                // If the new step is greater than or equal to one
                if (newStep >= 0) {

                    // Trigger the tour step change
                    this.onTourStepChange(newStep, oldStep)
                }
            },

            /*
            * Upon touring value change
            *
            * @param {boolean} touring
            * @returns {void}
            */
            touring(touring) {

                // Determine if touring
                if (touring) {

                    // Determine if the tour step is less than one
                    if (this.tourStep < 0) {

                        // Set the map to the initial position, showing the welcome modal
                        this.setInitialMapPosition()
                    }
                } else {

                    // Clear the map elements
                    this.clearMap()

                    // There is no tour ongoing (user is exploring on their own), show all modal markers
                    this.setAllMarkers()
                }
            }
        }
    }
</script>